import { useMemo } from 'react';
import { compact } from 'lodash';
import { habitatDiversityOptions, BIRD_ACTIVITY } from '../marketplace-custom-config';
import { formatSourcesList } from '../util/data';

const useLandDetails = ({ landDetails, categories, intl, acreage }) => {
  const features = useMemo(() => {
    if (!landDetails) return [];

    const {
      foodSources,
      waterSources,
      shelter,
      hasFoodSources,
      hasWaterSources,
      hasShelter,
      foodSourcesInfo,
      waterSourcesInfo,
      shelterInfo,
      habitatDiversity,
      habitatDiversityInfo,
      hasNationalParks,
      hasWildlifeRefuges,
    } = landDetails;

    const hasHabitatDiversity = habitatDiversity && habitatDiversity.length > 0;
    const shouldRender =
      hasShelter || hasWaterSources || hasFoodSources || hasHabitatDiversity || acreage;

    if (!shouldRender) return [];

    const result = [];
    const landItems = [];

    // Acreage
    if (acreage) {
      landItems.push({
        iconVariant: 'GridSolid',
        label: intl.formatMessage({ id: 'ListingPage.keyInfoAcreage' }, { acres: acreage }),
      });
    }

    const SourceListFormatter = (sources, labelsMap) => {
      const formattedSources = sources.map(
        source =>
          labelsMap.find(labelObject => source === labelObject.key) &&
          labelsMap.find(labelObject => source === labelObject.key).label
      );
      return compact(formattedSources).join(' • ');
    };

    // Food Sources
    if (hasFoodSources && foodSources?.length > 0) {
      const foodSourcesList = formatSourcesList(foodSources);
      landItems.push({
        iconVariant: 'Food',
        label: foodSourcesList,
        subtitle: foodSourcesInfo || undefined,
      });
    }

    // Water Sources
    if (hasWaterSources && waterSources?.length > 0) {
      const waterSourcesList = formatSourcesList(waterSources);
      landItems.push({
        iconVariant: 'Water',
        label: waterSourcesList,
        subtitle: waterSourcesInfo || undefined,
      });
    }

    // Shelter
    if (hasShelter && shelter?.length > 0) {
      const shelterSourcesList = formatSourcesList(shelter);
      landItems.push({
        iconVariant: 'Shelter',
        label: shelterSourcesList,
        subtitle: shelterInfo || undefined,
      });
    }

    // Habitat Diversity
    if (hasHabitatDiversity) {
      const diversityList = SourceListFormatter(habitatDiversity, habitatDiversityOptions);
      landItems.push({
        iconVariant: 'LeafMaple',
        label: intl.formatMessage({ id: 'ListingPage.landDetailsHabitatDiversity' }),
        subtitle: diversityList + (habitatDiversityInfo ? `: ${habitatDiversityInfo}` : ''),
      });
    }

    // Public Access (for bird watching)
    if (categories.includes(BIRD_ACTIVITY)) {
      const publicAccessInfo = [];

      publicAccessInfo.push(
        `${intl.formatMessage({ id: 'ListingPage.landDetailsNationalParksLabel' })}: ${
          hasNationalParks
            ? intl.formatMessage({ id: 'FieldBoolean.yes' })
            : intl.formatMessage({ id: 'FieldBoolean.no' })
        }`
      );

      publicAccessInfo.push(
        `${intl.formatMessage({ id: 'ListingPage.landDetailsNationalWildlifeLabel' })}: ${
          hasWildlifeRefuges
            ? intl.formatMessage({ id: 'FieldBoolean.yes' })
            : intl.formatMessage({ id: 'FieldBoolean.no' })
        }`
      );

      landItems.push({
        iconVariant: 'MapSolid',
        label: intl.formatMessage({ id: 'ListingPage.landDetailsPublicAccess' }),
        subtitle: publicAccessInfo.join(' • '),
      });
    }

    if (landItems.length > 0) {
      result.push({
        heading: intl.formatMessage({ id: 'ListingPage.propertyFeaturesTitle' }),
        items: landItems,
      });
    }

    return result;
  }, [landDetails, categories, intl, acreage]);

  return { features };
};

export default useLandDetails;
